import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useCandidatesAddedByCampaignReportQuery } from "../../../App/Queries/ReportingQueries";
import { ICandidatesAddedByCampaignReport } from "../../../App/Typings/Reports/CandidatesAddedByCampaign";
import _ from "lodash";

export function getPreviousTimespan(startDate: Date, endDate: Date) {
  const timespanInMilliseconds = endDate.valueOf() - startDate.valueOf();
  const prevStartDate = new Date(startDate.valueOf() - timespanInMilliseconds);
  const prevEndDate = new Date(endDate.valueOf() - timespanInMilliseconds);
  return { prevStartDate, prevEndDate };
}

export type CandidatesAddedByCampaignReportState = ReturnType<typeof useCandidatesAddedByCampaignReportState>;

export const useCandidatesAddedByCampaignReportState = (
  startDate: Date,
  endDate: Date,
  campaignId?: string
) => {
  const numDaysInReport = useMemo(() => {
    return Math.round((endDate.valueOf() - startDate.valueOf()) / (1000 * 60 * 60 * 24));
  }, [endDate, startDate]);

  const { data: reportData, isLoading } = useCandidatesAddedByCampaignReportQuery(
    startDate,
    endDate,
    campaignId
  );

  const { isLoadingPrevReport, prevEndDate, prevStartDate, percentChange, previousCount } =
    usePreviousReportData(startDate, endDate, campaignId, reportData);

  return {
    getters: {
      isLoading,
      isReportEnabled: true,
      numDaysInReport,
      reportData,
      startDate,
      endDate,
      isLoadingPrevReport,
      prevEndDate,
      previousCount,
      prevStartDate,
      percentChange,
    },
  } as const;
};

const usePreviousReportData = (
  startDate: Date,
  endDate: Date,
  campaignId?: string,
  reportData?: ICandidatesAddedByCampaignReport | null
) => {
  const location = useLocation();
  const enablePrevReportQuery = location.pathname.includes("/");
  const { prevStartDate, prevEndDate } = getPreviousTimespan(startDate, endDate);

  const { data: prevReportData, isLoading: isLoadingPrevReport } = useCandidatesAddedByCampaignReportQuery(
    prevStartDate,
    prevEndDate,
    campaignId,
    !!campaignId && enablePrevReportQuery
  );

  const previousCount =
    prevReportData?.campaignsInReport.reduce((acc, curr) => acc + curr.countInReportPeriod, 0) || 0;
  const percentChange = getPercentChange("countInReportPeriod", prevReportData, reportData);

  return {
    isLoadingPrevReport,
    percentChange,
    prevEndDate,
    prevStartDate,
    previousCount,
  };
};

function getPercentChange(
  dataKey: string,
  prev?: ICandidatesAddedByCampaignReport | null,
  current?: ICandidatesAddedByCampaignReport | null
) {
  if (!prev || !current) {
    return 0;
  }
  const previousCount = prev.campaignsInReport.reduce((acc, curr) => acc + curr[dataKey], 0);
  const currentCount = current.campaignsInReport.reduce((acc, curr) => acc + curr[dataKey], 0);
  const percentChange = !previousCount
    ? currentCount * 100
    : ((currentCount - previousCount) / previousCount) * 100;
  return _.round(percentChange, 2);
}
